import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Center,
  Button,
  Input,
  Spinner,
  Pressable,
  useToast,
  Select,
  CheckIcon,
} from "native-base";
import { StyleSheet, View, PanResponder, Platform } from "react-native";
import { GlobalStyles } from "../../constants/styles";
import { useSelector } from "react-redux";
import axios from "axios";
import Toast from "../../components/Toast";
import { GlobalConstants } from "../../constants/constants";

const LOYALTY_API_BASE_URL = `${GlobalConstants.LoyaltyBaseUrl}`.replace(/\/$/, "");
const SLIDER_MIN = 0;
const SLIDER_MAX = 10000;
const SLIDER_MIN_DISTANCE = 10;

const styles = StyleSheet.create({
  pageBg: { backgroundColor: GlobalStyles.colors.lightGrey },
});

const gradientStyle = {
  background: "linear-gradient(90deg, #000561 0%, #000AC7 100%)",
  backgroundColor: GlobalStyles.colors.gradientBlueStart,
};

const titleCase = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());

const safeNum = (v, fallback = 0) => {
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : fallback;
};
const safeInt = (v, fallback = 0) => {
  const n = typeof v === "number" ? v : parseInt(String(v), 10);
  return Number.isFinite(n) ? n : fallback;
};
const ensureBoolOrDefault = (v, fallback) =>
  typeof v === "boolean" ? v : fallback;
const ensureObjectOrDefault = (v, fallback = {}) =>
  v && typeof v === "object" && !Array.isArray(v) ? v : fallback;
const unwrap = (res) =>
  res && typeof res === "object" && "status" in res
    ? res
    : { status: true, data: res };
const is404 = (err) => err?.response?.status === 404;
const formatPointsLike = (val, fallback = "0.00") => {
  const n = safeNum(String(val ?? "").trim(), NaN);
  return Number.isFinite(n) ? n.toFixed(2) : fallback;
};
const formatWithCommas = (v) => {
  const n = safeNum(v, 0);
  return Math.round(n).toLocaleString();
};

const makeApiCall = async (method, endpoint, data = null, token, retries = 2) => {
  const url = `${LOYALTY_API_BASE_URL}${endpoint}`;
  const config = {
    method,
    url,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    timeout: 30000,
  };
  if (data && (method === "POST" || method === "PUT" || method === "PATCH"))
    config.data = data;
  else if (data && method === "GET") config.params = data;
  try {
    const response = await axios(config);
    return response.data;
  } catch (error) {
    if (
      retries > 0 &&
      (error.code === "ECONNABORTED" || error.message.includes("timeout"))
    ) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return makeApiCall(method, endpoint, data, token, retries - 1);
    }
    throw error;
  }
};

const configurationApi = {
  getUserConfig: (token) =>
    makeApiCall("GET", "/api/configuration/user", null, token),
  updateUserConfig: (token, configData) =>
    makeApiCall("PUT", "/api/configuration/user", configData, token),
  getTierConfig: (token) =>
    makeApiCall("GET", "/api/configuration/tier-config", null, token),
  saveTierConfig: (token, tiers) =>
    makeApiCall("POST", "/api/configuration/tier-config", { tiers }, token),
  getUserLoyaltyPoints: (token) =>
    makeApiCall("GET", "/api/configuration/user-loyalty-points", null, token),

  getCommonPoolSettings: (token) =>
    makeApiCall("GET", "/api/configuration/common-pool", null, token),
  updateCommonPoolSettings: (token, joinCommonPool) =>
    makeApiCall(
      "PUT",
      "/api/configuration/common-pool",
      { join_tt_common_pool: Boolean(joinCommonPool) },
      token,
    ),
  lockTier: (token, tierNumber) =>
    makeApiCall(
      "POST",
      `/api/configuration/tier-config/${tierNumber}/lock`,
      null,
      token,
    ),
  unlockTier: (token, tierNumber) =>
    makeApiCall(
      "POST",
      `/api/configuration/tier-config/${tierNumber}/unlock`,
      null,
      token,
    ),
};

const dashboardApi = {
  getUserDashboard: (token, period = "all") =>
    makeApiCall("GET", "/api/dashboard/user", { period }, token),
};

const pointsApi = {
  issue: (token, payload) =>
    makeApiCall("POST", "/api/points/issue", payload, token),

  redeem: (token, pointsToRedeem, qrCodeData = null) => {
    const body = { points_to_redeem: safeNum(pointsToRedeem, NaN) };
    if (qrCodeData) body.qr_code_data = String(qrCodeData);
    return makeApiCall("POST", "/api/points/redeem", body, token);
  },
  getUserPointsSummary: (token, userId) =>
    makeApiCall("GET", `/api/points/user/points/${userId}`, null, token),
};

const YesNoGradientToggle = ({ value, onChange, isDisabled }) => (
  <HStack space={0} opacity={isDisabled ? 0.6 : 1}>
    <Pressable onPress={() => !isDisabled && onChange(true)}>
      <Box
        w={20}
        h={8}
        alignItems="center"
        justifyContent="center"
        borderTopLeftRadius={2}
        borderBottomLeftRadius={2}
        borderWidth={1}
        borderColor={GlobalStyles.colors.blue}
        style={
          value === true
            ? gradientStyle
            : { backgroundColor: GlobalStyles.colors.white }
        }
      >
        <Text
          style={{
            color:
              value === true
                ? GlobalStyles.colors.white
                : GlobalStyles.colors.blue,
            fontWeight: "bold",
            fontSize: 14,
            fontFamily: GlobalStyles.text.fontFamily,
          }}
        >
          Yes
        </Text>
      </Box>
    </Pressable>
    <Pressable onPress={() => !isDisabled && onChange(false)}>
      <Box
        w={20}
        h={8}
        alignItems="center"
        justifyContent="center"
        borderTopRightRadius={2}
        borderBottomRightRadius={2}
        borderWidth={1}
        borderColor={GlobalStyles.colors.blue}
        style={
          value === false
            ? gradientStyle
            : { backgroundColor: GlobalStyles.colors.white }
        }
      >
        <Text
          style={{
            color:
              value === false
                ? GlobalStyles.colors.white
                : GlobalStyles.colors.blue,
            fontWeight: "bold",
            fontSize: 14,
            fontFamily: GlobalStyles.text.fontFamily,
          }}
        >
          No
        </Text>
      </Box>
    </Pressable>
  </HStack>
);

const SLIDER_GRADIENT_STYLE = {
  background: "linear-gradient(90deg, #0F135C 0%, #000AC7 100%)",
  backgroundColor: "#000AC7", 
};
const SLIDER_TRACK_BG = "#D9D9D9";
const LOCK_GREY = "#9E9E9E";
const TIP_BORDER_COLOR = "#000AC7";
const TOOLTIP_BG = "#0F135C"; 

const TICK_SIZE = 10;
const TICK_SIZE_OPEN = 22;
const TRACK_HEIGHT = 15;
const TRACK_TOP = 30;

const TierBoundarySlider = ({
  boundaries,
  onChangeBoundaries,
  lockedBoundaries = [],
  min = SLIDER_MIN,
  max = SLIDER_MAX,
  step = 1,
  minDistance = SLIDER_MIN_DISTANCE,
  disabled = false,
}) => {
  const trackWidthRef = useRef(1);
  const trackPageXRef = useRef(0);
  const activeIndexRef = useRef(null);
  const boundariesRef = useRef(boundaries);
  const lockedRef = useRef(lockedBoundaries);

  const [openIndex, setOpenIndex] = useState(null); 
  const [draft, setDraft] = useState("");

  useEffect(() => {
    boundariesRef.current = boundaries;
  }, [boundaries]);
  useEffect(() => {
    lockedRef.current = lockedBoundaries;
  }, [lockedBoundaries]);
  useEffect(() => {
    if (openIndex != null && boundaries[openIndex] != null) {
      setDraft(String(Math.round(boundaries[openIndex])));
    }
  }, [openIndex, boundaries]);

  const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
  const snap = (n) => (step > 0 ? Math.round(n / step) * step : n);
  const valueToPx = (v) => {
    const t = (v - min) / Math.max(1, max - min);
    return clamp(t, 0, 1) * trackWidthRef.current;
  };
  const pxToValue = (px) => {
    const t = clamp(px / Math.max(1, trackWidthRef.current), 0, 1);
    return min + t * (max - min);
  };

  const pickNearestUnlockedIndex = (xPx) => {
    const list = boundariesRef.current || [];
    const lockedList = lockedRef.current || [];
    let bestIdx = null;
    let bestDist = Infinity;
    list.forEach((v, i) => {
      if (lockedList[i]) return;
      const d = Math.abs(xPx - valueToPx(v));
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    });
    return bestIdx;
  };

  const wallsFor = (idx, list) => {
    const leftWall = idx === 0 ? min : list[idx - 1];
    const rightWall = idx === list.length - 1 ? max : list[idx + 1];
    return { leftWall, rightWall };
  };

  const updateFromPageX = (pageX) => {
    const idx = activeIndexRef.current;
    if (idx == null) return;
    const list = boundariesRef.current || [];
    const xPx = pageX - trackPageXRef.current;
    const raw = clamp(snap(pxToValue(xPx)), min, max);
    const { leftWall, rightWall } = wallsFor(idx, list);
    const clamped = clamp(raw, leftWall + minDistance, rightWall - minDistance);

    const next = [...list];
    next[idx] = clamped;
    onChangeBoundaries(next);
  };

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => !disabled,
        onMoveShouldSetPanResponder: (evt, gesture) =>
          !disabled && (Math.abs(gesture.dx) > 2 || Math.abs(gesture.dy) > 2),
        onPanResponderGrant: (evt) => {
          if (disabled) return;
          const xPx = evt.nativeEvent.pageX - trackPageXRef.current;
          const idx = pickNearestUnlockedIndex(xPx);
          activeIndexRef.current = idx;
          if (idx != null) setOpenIndex(idx);
        },
        onPanResponderMove: (evt) => {
          if (disabled) return;
          if (activeIndexRef.current != null) updateFromPageX(evt.nativeEvent.pageX);
        },
        onPanResponderTerminationRequest: () => false,
        onShouldBlockNativeResponder: () => true,
        onPanResponderRelease: () => {
          activeIndexRef.current = null;
        },
        onPanResponderTerminate: () => {
          activeIndexRef.current = null;
        },
      }),
    [disabled, lockedBoundaries, min, max, step, minDistance],
  );

  const handleTapTick = (idx) => {
    if (disabled) return;
    setOpenIndex((prev) => (prev === idx ? null : idx));
  };

  const handleDraftChange = (text) => {
    setDraft(text.replace(/[^0-9]/g, ""));
  };

  const commitDraft = () => {
    if (openIndex == null) return;
    const list = boundariesRef.current || [];
    const parsedRaw = parseFloat(draft);
    const parsed = Number.isFinite(parsedRaw) ? parsedRaw : list[openIndex];
    const { leftWall, rightWall } = wallsFor(openIndex, list);
    const clamped = clamp(snap(parsed), leftWall + minDistance, rightWall - minDistance);

    const next = [...list];
    next[openIndex] = clamped;
    onChangeBoundaries(next);
    setDraft(String(clamped));
  };

  const list = Array.isArray(boundaries) ? boundaries : [];
  const span = Math.max(1, max - min);
  const toPct = (v) => ((v - min) / span) * 100;
  const fillEndPct = list.length ? toPct(list[list.length - 1]) : 100;

  return (
    <View style={{ width: "100%" }}>
      <View
        {...panResponder.panHandlers}
        style={{ height: 68, justifyContent: "flex-start" }}
      >
        <View
          ref={(ref) => {
            if (!ref) return;
            requestAnimationFrame(() => {
              try {
                ref.measure((x, y, w, h, pageX) => {
                  trackWidthRef.current = Math.max(1, w);
                  trackPageXRef.current = pageX;
                });
              } catch { }
            });
          }}
          onLayout={(e) => {
            trackWidthRef.current = Math.max(1, e.nativeEvent.layout.width);
          }}
          style={{
            position: "absolute",
            top: TRACK_TOP,
            left: 0,
            right: 0,
            height: TRACK_HEIGHT,
            borderRadius: TRACK_HEIGHT / 2,
            backgroundColor: SLIDER_TRACK_BG,
            overflow: "hidden",
          }}
        >
          <View
            style={[
              {
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: `${clamp(fillEndPct, 0, 100)}%`,
                borderRadius: TRACK_HEIGHT / 2,
                opacity: disabled ? 0.6 : 1,
              },
              SLIDER_GRADIENT_STYLE,
            ]}
          />
        </View>

        {list.map((v, i) => {
          const pct = toPct(v);
          const isLocked = Boolean(lockedBoundaries[i]);
          const isOpen = openIndex === i;
          const size = isOpen ? TICK_SIZE_OPEN : TICK_SIZE;
          const tickTop = TRACK_TOP + TRACK_HEIGHT / 2 - size / 2;

          return (
            <Pressable
              key={`boundary-${i}`}
              onPress={() => handleTapTick(i)}
              style={{
                position: "absolute",
                top: tickTop,
                left: `${clamp(pct, 0, 100)}%`,
                transform: [{ translateX: -size / 2 }],
                width: size,
                height: size,
              }}
            >
              <View
                style={{
                  width: size,
                  height: size,
                  borderRadius: size / 2,
                  backgroundColor: isLocked
                    ? LOCK_GREY
                    : isOpen
                      ? TIP_BORDER_COLOR
                      : GlobalStyles.colors.white,
                  borderWidth: isLocked ? 0 : 2,
                  borderColor: TIP_BORDER_COLOR,
                  shadowColor: "#000",
                  shadowOpacity: isOpen ? 0.2 : 0.1,
                  shadowRadius: isOpen ? 4 : 2,
                  shadowOffset: { width: 0, height: 1 },
                  elevation: isOpen ? 3 : 1,
                }}
              />
            </Pressable>
          );
        })}

        {openIndex != null && list[openIndex] != null && (
          <View
            pointerEvents="box-none"
            style={{
              position: "absolute",
              top: 0,
              left: `${clamp(toPct(list[openIndex]), 0, 100)}%`,
              transform: [{ translateX: -40 }],
              alignItems: "center",
            }}
          >
            <View
              style={{
                minWidth: 60,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: lockedBoundaries[openIndex]
                  ? LOCK_GREY
                  : TOOLTIP_BG,
                paddingHorizontal: 10,
                paddingVertical: 5,
                borderRadius: 8,
              }}
            >
              {lockedBoundaries[openIndex] ? (
                <Text
                  style={{
                    fontFamily: GlobalStyles.text.fontFamily,
                    fontSize: 13,
                    fontWeight: "500",
                    color: GlobalStyles.colors.white,
                  }}
                >
                  🔒 {formatWithCommas(list[openIndex])}
                </Text>
              ) : (
                <Input
                  value={draft}
                  onChangeText={handleDraftChange}
                  onEndEditing={commitDraft}
                  onSubmitEditing={commitDraft}
                  variant="unstyled"
                  textAlign="center"
                  color={GlobalStyles.colors.white}
                  fontWeight="500"
                  fontSize={13}
                  keyboardType="numeric"
                  p={0}
                  minW="40px"
                  style={{
                    fontFamily: GlobalStyles.text.fontFamily,
                    textAlign: "center",
                  }}
                />
              )}
            </View>
            <View
              style={{
                width: 7,
                height: 7,
                backgroundColor: lockedBoundaries[openIndex]
                  ? LOCK_GREY
                  : TOOLTIP_BG,
                transform: [{ rotate: "45deg" }],
                marginTop: -4,
              }}
            />
          </View>
        )}
      </View>

      <HStack flexWrap="wrap" mt={2} style={{ rowGap: 4, columnGap: 14 }}>
        {(() => {
          const segs = [];
          const bs = list;
          for (let i = 0; i <= bs.length; i++) {
            const lo = i === 0 ? min : bs[i - 1];
            const hi = i === bs.length ? max : bs[i];
            segs.push(
              <Text
                key={`seg-${i}`}
                style={{
                  fontFamily: GlobalStyles.text.fontFamily,
                  fontSize: 12,
                  color: "#666",
                }}
              >
                Tier {i + 1}:{" "}
                <Text style={{ color: GlobalStyles.colors.black, fontWeight: "500" }}>
                  {formatWithCommas(lo)}
                  {i === bs.length ? "+" : ` \u2013 ${formatWithCommas(hi)}`}
                </Text>
              </Text>,
            );
          }
          return segs;
        })()}
      </HStack>
    </View>
  );
};

const LoyaltySettingsStep1 = ({
  locked,
  joinCommonPool,
  setJoinCommonPool,
  haveTiers,
  setHaveTiers,
  pointsValue,
  setPointsValue,
  dollarsValue,
  setDollarsValue,
  totalPointsCap,
  setTotalPointsCap,
  onNext,
  onSave,
  isSaving,
}) => (
  <Center flex={1} style={styles.pageBg} px={4}>
    <Box
      width="100%"
      maxW={443}
      marginTop={30}
      borderRadius={12}
      backgroundColor={GlobalStyles.colors.white}
      alignSelf="center"
      shadow={2}
      p={6}
    >
      <VStack space={6} alignItems="center" w="100%">
        <Box
          w="100%"
          maxW={309}
          borderRadius={0}
          borderWidth={0.2}
          borderColor={GlobalStyles.colors.black}
          backgroundColor={GlobalStyles.colors.white}
          p={6}
          alignItems="flex-start"
          justifyContent="center"
        >
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 16,
              color: GlobalStyles.colors.black,
              fontWeight: 500,
              marginBottom: 4,
            }}
          >
            Are you joining the TT points common pool?
          </Text>
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 12,
              color: "#666",
              marginBottom: 14,
            }}
          >
            (TT pool join will allow bigger traffic)
          </Text>
          <YesNoGradientToggle
            value={joinCommonPool}
            onChange={setJoinCommonPool}
            isDisabled={locked}
          />
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 16,
              color: GlobalStyles.colors.black,
              fontWeight: 500,
              marginTop: 20,
              marginBottom: 8,
            }}
          >
            Have Tiers?
          </Text>
          <YesNoGradientToggle
            value={haveTiers}
            onChange={setHaveTiers}
            isDisabled={locked}
          />
        </Box>
        <Box
          w="100%"
          maxW={309}
          borderRadius={0}
          borderWidth={0.2}
          borderColor={GlobalStyles.colors.black}
          backgroundColor={GlobalStyles.colors.white}
          p={6}
          alignItems="flex-start"
          justifyContent="center"
        >
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 16,
              color: GlobalStyles.colors.black,
              fontWeight: 500,
              marginBottom: 14,
            }}
          >
            Cash To Points
          </Text>
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 14,
              color: GlobalStyles.colors.black,
              fontWeight: 500,
              marginBottom: 12,
            }}
          >
            Enter how many points for how many dollars
          </Text>

          <HStack alignItems="flex-end" space={4} mb={4}>
            <VStack alignItems="center" space={1}>
              <View
                style={{
                  width: 80,
                  height: 44,
                  borderWidth: 1,
                  borderColor: GlobalStyles.colors.blue,
                  justifyContent: "center",
                  alignItems: "center",
                  background: "linear-gradient(90deg, #000561 0%, #000AC7 100%)",
                  backgroundColor: GlobalStyles.colors.blue,
                }}
              >
                <Input
                  value={pointsValue}
                  onChangeText={locked ? undefined : setPointsValue}
                  isReadOnly={locked}
                  variant="unstyled"
                  textAlign="center"
                  color={GlobalStyles.colors.white}
                  fontWeight="bold"
                  fontSize={20}
                  keyboardType="numeric"
                  p={0}
                  style={{
                    fontFamily: GlobalStyles.text.fontFamily,
                    width: 78,
                    height: 42,
                    textAlign: "center",
                    color: GlobalStyles.colors.white,
                  }}
                />
              </View>
              <Text
                style={{
                  fontFamily: GlobalStyles.text.fontFamily,
                  fontSize: 13,
                  color: GlobalStyles.colors.black,
                  marginTop: 4,
                }}
              >
                Point
              </Text>
            </VStack>

            <Text
              style={{
                fontFamily: GlobalStyles.text.fontFamily,
                fontSize: 16,
                color: GlobalStyles.colors.black,
                fontWeight: "500",
                marginBottom: 22,
              }}
            >
              for
            </Text>

            <VStack alignItems="center" space={1}>
              <View
                style={{
                  width: 80,
                  height: 44,
                  borderWidth: 1,
                  borderColor: GlobalStyles.colors.blue,
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: GlobalStyles.colors.white,
                }}
              >
                <Input
                  value={dollarsValue}
                  onChangeText={locked ? undefined : setDollarsValue}
                  isReadOnly={locked}
                  variant="unstyled"
                  textAlign="center"
                  color={GlobalStyles.colors.blue}
                  fontWeight="bold"
                  fontSize={20}
                  keyboardType="numeric"
                  p={0}
                  style={{
                    fontFamily: GlobalStyles.text.fontFamily,
                    width: 78,
                    height: 42,
                    textAlign: "center",
                    color: GlobalStyles.colors.blue,
                  }}
                />
              </View>
              <Text
                style={{
                  fontFamily: GlobalStyles.text.fontFamily,
                  fontSize: 13,
                  color: GlobalStyles.colors.black,
                  marginTop: 4,
                }}
              >
                Dollar
              </Text>
            </VStack>
          </HStack>

          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 16,
              color: GlobalStyles.colors.black,
              fontWeight: "500",
              marginBottom: 2,
            }}
          >
            Total Points
          </Text>
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 12,
              color: "#666",
              marginBottom: 8,
            }}
          >
            (Enter if there is a cap on the Total Points)
          </Text>
          <View
            style={{
              width: 140,
              height: 44,
              borderWidth: 1,
              borderColor: GlobalStyles.colors.blue,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: GlobalStyles.colors.white,
            }}
          >
            <Input
              value={totalPointsCap}
              onChangeText={locked ? undefined : setTotalPointsCap}
              isReadOnly={locked}
              variant="unstyled"
              textAlign="center"
              color={GlobalStyles.colors.black}
              fontWeight="bold"
              fontSize={18}
              keyboardType="numeric"
              placeholder="No cap"
              p={0}
              style={{
                fontFamily: GlobalStyles.text.fontFamily,
                width: 138,
                height: 42,
                textAlign: "center",
                color: GlobalStyles.colors.black,
              }}
            />
          </View>
        </Box>

        {haveTiers === false ? (
          <Button
            mt={2}
            alignSelf="center"
            w={48}
            h={12}
            borderRadius={6}
            bg={GlobalStyles.colors.blue}
            _text={{
              color: GlobalStyles.colors.white,
              fontFamily: GlobalStyles.text.fontFamily,
              fontWeight: "bold",
              fontSize: 16,
            }}
            onPress={onSave}
            isLoading={isSaving}
            isLoadingText="Saving..."
            isDisabled={
              haveTiers === null || !pointsValue || !dollarsValue
            }
          >
            Save Configuration
          </Button>
        ) : (
          <Button
            mt={2}
            alignSelf="center"
            w={40}
            h={12}
            borderRadius={6}
            bg={GlobalStyles.colors.blue}
            _text={{
              color: GlobalStyles.colors.white,
              fontFamily: GlobalStyles.text.fontFamily,
              fontWeight: "bold",
              fontSize: 16,
            }}
            onPress={onNext}
            isDisabled={
              haveTiers === null || !pointsValue || !dollarsValue
            }
          >
            Next
          </Button>
        )}
      </VStack>
    </Box>
  </Center>
);

const TiersSetupCard = ({
  locked,
  numTiersStr,
  setNumTiersStr,
  tierBoundaries,
  setTierBoundaries,
  tierNames,
  lockedTiers,
  lockingTierNumber,
  onLockTier,
  onUnlockTier,
  onNext,
  onBack,
}) => {
  const numTiers = Math.max(2, Math.min(10, safeInt(numTiersStr, 3)));

  return (
    <Center flex={1} style={styles.pageBg} px={4}>
      <Box
        w="100%"
        maxW={492}
        borderWidth={1}
        borderColor={GlobalStyles.colors.black}
        backgroundColor="#D9D9D940"
        mt={8}
        alignSelf="center"
        overflow="hidden"
      >
        <HStack
          bg="#D9D9D9"
          borderBottomWidth={0.2}
          borderBottomColor={GlobalStyles.colors.black}
          h="52px"
          alignItems="center"
          justifyContent="space-between"
          px={6}
        >
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 18,
              color: GlobalStyles.colors.black,
              fontWeight: 500,
            }}
          >
            How many Tiers?
          </Text>
          <View
            style={{
              width: 64,
              height: 40,
              borderWidth: 2,
              borderColor: GlobalStyles.colors.blue,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: GlobalStyles.colors.white,
              borderRadius: 2,
            }}
          >
            <Input
              value={numTiersStr}
              onChangeText={(t) => {
                if (locked) return;
                const clean = t.replace(/[^0-9]/g, "").slice(0, 2);
                setNumTiersStr(clean);
              }}
              isReadOnly={locked}
              variant="unstyled"
              textAlign="center"
              color={GlobalStyles.colors.blue}
              fontWeight="bold"
              fontSize={20}
              keyboardType="numeric"
              maxLength={2}
              p={0}
              style={{
                fontFamily: GlobalStyles.text.fontFamily,
                width: 60,
                height: 38,
                textAlign: "center",
                color: GlobalStyles.colors.blue,
              }}
            />
          </View>
        </HStack>

        <VStack px={6} py={6} space={4}>
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 16,
              color: GlobalStyles.colors.black,
              fontWeight: 500,
            }}
          >
            Select Tiers Points
          </Text>
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 12,
              color: "#666",
              marginBottom: 2,
            }}
          >
            Drag any tick to size a tier, or tap it to type an exact value.
            Tap a chip below to lock a boundary in place — it turns into a
            fixed grey marker until you unlock it again.
          </Text>
          <TierBoundarySlider
            boundaries={tierBoundaries}
            onChangeBoundaries={locked ? () => {} : setTierBoundaries}
            lockedBoundaries={lockedTiers}
            min={SLIDER_MIN}
            max={SLIDER_MAX}
            step={1}
            minDistance={SLIDER_MIN_DISTANCE}
            disabled={locked}
          />
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 13,
              color: "#666",
              textAlign: "center",
              marginTop: 4,
            }}
          >
            {numTiers} Tiers — Tier 1 always starts at {SLIDER_MIN}, Tier{" "}
            {numTiers} has no upper limit
          </Text>

          <VStack space={2} mt={2}>
            <Text
              style={{
                fontFamily: GlobalStyles.text.fontFamily,
                fontSize: 13,
                color: GlobalStyles.colors.black,
                fontWeight: 500,
              }}
            >
              Lock Tier
            </Text>
            <HStack flexWrap="wrap" style={{ rowGap: 8, columnGap: 8 }}>
              {Array.from({ length: numTiers - 1 }).map((_, idx) => {
                const tierNumber = idx + 1;
                const isLocked = Boolean(lockedTiers?.[idx]);
                return (
                  <Pressable
                    key={`lock-tier-${tierNumber}`}
                    onPress={() =>
                      isLocked ? onUnlockTier(tierNumber) : onLockTier(tierNumber)
                    }
                    isDisabled={locked || lockingTierNumber === tierNumber}
                  >
                    <HStack
                      alignItems="center"
                      space={1}
                      px={3}
                      py={1.5}
                      borderRadius={16}
                      borderWidth={1}
                      borderColor={isLocked ? LOCK_GREY : GlobalStyles.colors.blue}
                      backgroundColor={isLocked ? "#9E9E9E1A" : "transparent"}
                    >
                      <Text style={{ fontSize: 12 }}>{isLocked ? "🔒" : "🔓"}</Text>
                      <Text
                        style={{
                          fontFamily: GlobalStyles.text.fontFamily,
                          fontSize: 12,
                          fontWeight: "500",
                          color: isLocked ? LOCK_GREY : TIP_BORDER_COLOR,
                        }}
                      >
                        {lockingTierNumber === tierNumber
                          ? "..."
                          : `Tier ${tierNumber}`}
                      </Text>
                    </HStack>
                  </Pressable>
                );
              })}
            </HStack>
          </VStack>

          <HStack justifyContent="space-between" mt={2}>
            <Button
              variant="outline"
              borderColor={GlobalStyles.colors.blue}
              _text={{ color: GlobalStyles.colors.blue, fontWeight: "bold", fontSize: 15 }}
              onPress={onBack}
              w={100}
              h={12}
              borderRadius={6}
            >
              Back
            </Button>
            <Button
              bg={GlobalStyles.colors.blue}
              _text={{ color: GlobalStyles.colors.white, fontWeight: "bold", fontSize: 15 }}
              onPress={onNext}
              w={100}
              h={12}
              borderRadius={6}
            >
              Next
            </Button>
          </HStack>
        </VStack>
      </Box>
    </Center>
  );
};

const NameTiersCard = ({
  locked,
  numTiers,
  tierNames,
  setTierNames,
  onBack,
  onSave,
  isSaving,
}) => {
  const defaultPlaceholders = ["Silver", "Gold", "Diamond", "Platinum", "Elite", "Sapphire"];

  const handleChange = (i, val) => {
    if (locked) return;
    const next = [...tierNames];
    next[i] = val;
    setTierNames(next);
  };

  return (
    <Center flex={1} style={styles.pageBg} px={4}>
      <Box
        w="100%"
        maxW={600}
        bg={GlobalStyles.colors.white}
        borderRadius={12}
        mt={8}
        alignSelf="center"
        shadow={2}
        px={8}
        py={6}
      >
        <Text
          style={{
            fontFamily: GlobalStyles.text.fontFamily,
            fontSize: 18,
            color: GlobalStyles.colors.black,
            fontWeight: "500",
            marginBottom: 12,
          }}
        >
          Name Tiers
        </Text>
        <HStack
          justifyContent="center"
          alignItems="flex-start"
          space={0}
          py={2}
        >
          <VStack>
            {Array.from({ length: numTiers }).map((_, i) => (
              <Box
                key={`num-${i}`}
                w="120px"
                h="36px"
                borderWidth={1}
                borderColor="#2E2E2E"
                bg={GlobalStyles.colors.white}
                alignItems="center"
                justifyContent="center"
                mt={i === 0 ? 0 : -1}
              >
                <Text
                  style={{
                    fontFamily: GlobalStyles.text.fontFamily,
                    fontSize: 16,
                    color: GlobalStyles.colors.black,
                  }}
                >
                  {i + 1}
                </Text>
              </Box>
            ))}
          </VStack>
          <VStack>
            {Array.from({ length: numTiers }).map((_, i) => (
              <Box
                key={`name-${i}`}
                w="120px"
                h="36px"
                borderWidth={1}
                borderColor="#2E2E2E"
                bg={GlobalStyles.colors.white}
                mt={i === 0 ? 0 : -1}
                px={3}
                justifyContent="center"
              >
                <Input
                  value={tierNames[i] ?? ""}
                  onChangeText={(val) => handleChange(i, val)}
                  isReadOnly={locked}
                  variant="unstyled"
                  placeholder={defaultPlaceholders[i] || `Tier ${i + 1}`}
                  color={GlobalStyles.colors.black}
                  fontSize={15}
                  textAlign="center"
                  style={{ fontFamily: GlobalStyles.text.fontFamily }}
                />
              </Box>
            ))}
          </VStack>
        </HStack>
        <HStack justifyContent="space-between" w="100%" mt={6}>
          <Button
            variant="outline"
            borderColor={GlobalStyles.colors.blue}
            _text={{ color: GlobalStyles.colors.blue, fontWeight: "bold", fontSize: 15 }}
            onPress={onBack}
            w={100}
            h={12}
            borderRadius={6}
          >
            Back
          </Button>
          <Button
            bg={GlobalStyles.colors.blue}
            _text={{
              color: GlobalStyles.colors.white,
              fontFamily: GlobalStyles.text.fontFamily,
              fontWeight: "bold",
              fontSize: 16,
            }}
            onPress={onSave}
            isLoading={isSaving}
            isLoadingText="Saving..."
            w={100}
            h={12}
            borderRadius={6}
          >
            Save
          </Button>
        </HStack>
      </Box>
    </Center>
  );
};

const resizeBoundaries = (prev, targetLen, max = SLIDER_MAX) => {
  const list = Array.isArray(prev) ? prev : [];
  if (targetLen <= 0) return [];
  if (targetLen === list.length) return list;
  if (targetLen < list.length) return list.slice(0, targetLen);
  const start = list.length ? list[list.length - 1] : 0;
  const toAdd = targetLen - list.length;
  const step = (max - start) / (toAdd + 1);
  const extra = Array.from({ length: toAdd }, (_, i) =>
    Math.round(start + step * (i + 1)),
  );
  return [...list, ...extra];
};

const boundariesToTiersPayload = (boundaries, numTiers, tierNames, max = SLIDER_MAX) => {
  return Array.from({ length: numTiers }, (_, i) => {
    const lo = i === 0 ? 0 : boundaries[i - 1];
    const hi = i === numTiers - 1 ? max : boundaries[i];
    return {
      tier_name: (tierNames[i] || `Tier ${i + 1}`).trim(),
      min_points: Math.round(i === 0 ? 0 : lo + 1),
      max_points: Math.round(hi),
      multiplier: parseFloat((1.0 + i * 0.25).toFixed(2)),
    };
  });
};

const LoyaltySettings = ({ toast, user }) => {
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [joinCommonPool, setJoinCommonPool] = useState(false);
  const [haveTiers, setHaveTiers] = useState(null);
  const [pointsValue, setPointsValue] = useState("2");
  const [dollarsValue, setDollarsValue] = useState("1");
  const [totalPointsCap, setTotalPointsCap] = useState("");

  const [numTiersStr, setNumTiersStrRaw] = useState("3");

  const [tierBoundaries, setTierBoundaries] = useState([500, 1500]);
  const [tierNames, setTierNames] = useState([
    "Silver", "Gold", "Diamond", "Platinum", "Elite", "Sapphire",
  ]);
  const [lockedTiers, setLockedTiers] = useState([]);
  const [lockingTierNumber, setLockingTierNumber] = useState(null);

  const locked = false;
  const setNumTiersStr = (val) => {
    setNumTiersStrRaw(val);
    const n = Math.max(2, Math.min(10, safeInt(val, 3)));
    setTierBoundaries((prev) => resizeBoundaries(prev, n - 1));
    setLockedTiers((prev) => {
      const next = [...prev];
      next.length = n - 1;
      return Array.from({ length: n - 1 }, (_, i) => Boolean(next[i]));
    });
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setIsLoading(true);
      const [cfgRaw, tierRaw, poolRaw] = await Promise.all([
        configurationApi.getUserConfig(user.token),
        configurationApi.getTierConfig(user.token).catch(() => null),
        configurationApi.getCommonPoolSettings(user.token).catch(() => null),
      ]);

      const cfgRes = unwrap(cfgRaw);
      if (cfgRes.status && cfgRes.data) {
        const c = cfgRes.data;
        setHaveTiers(ensureBoolOrDefault(c.tiers_enabled, null));
        if (c.points_per_dollar_points != null)
          setPointsValue(String(c.points_per_dollar_points));
        if (c.points_per_dollar_dollars != null)
          setDollarsValue(String(c.points_per_dollar_dollars));
        if (c.number_of_tiers && c.number_of_tiers > 0)
          setNumTiersStrRaw(String(c.number_of_tiers));
        if (c.total_points_cap != null)
          setTotalPointsCap(String(c.total_points_cap));
        if (poolRaw == null && c.join_tt_common_pool != null) {
          setJoinCommonPool(ensureBoolOrDefault(c.join_tt_common_pool, false));
        }
      }

      if (poolRaw) {
        const poolRes = unwrap(poolRaw);
        if (poolRes.status && poolRes.data) {
          setJoinCommonPool(
            ensureBoolOrDefault(poolRes.data.join_tt_common_pool, false),
          );
        }
      }

      if (tierRaw) {
        const tierRes = unwrap(tierRaw);
        if (tierRes.status && tierRes.data) {

          const tiers = [...(tierRes.data.tiers || [])].sort(
            (a, b) => safeNum(a.min_points, 0) - safeNum(b.min_points, 0),
          );
          if (tiers.length > 0) {
            setNumTiersStrRaw(String(tiers.length));
            setTierNames(tiers.map((t) => t.tier_name || ""));
            setLockedTiers(tiers.slice(0, -1).map((t) => Boolean(t.is_locked)));

            const boundaries = tiers
              .slice(0, -1)
              .map((t) =>
                Math.max(SLIDER_MIN, Math.min(safeNum(t.max_points, 0), SLIDER_MAX)),
              );
            setTierBoundaries(boundaries);
          }
        }
      }

      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message={"Configuration loaded successfully!"}
          />
        ),
        placement: "top",
        duration: 2500,
      });
    } catch (error) {
      console.error("Error loading configuration:", error);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Failed to load configuration. Using defaults."}
          />
        ),
        placement: "top",
        duration: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const persistTierLayoutAndToggle = async (tierNumber, makeLocked) => {
    const idx = tierNumber - 1;
    const numTiers = Math.max(2, Math.min(10, safeInt(numTiersStr, 3)));
    try {
      setLockingTierNumber(tierNumber);

      const tiersPayload = boundariesToTiersPayload(
        tierBoundaries,
        numTiers,
        tierNames,
      );
      await configurationApi.saveTierConfig(user.token, tiersPayload);

      const raw = makeLocked
        ? await configurationApi.lockTier(user.token, tierNumber)
        : await configurationApi.unlockTier(user.token, tierNumber);
      const res = unwrap(raw);
      if (!res.status)
        throw new Error(
          res.status_msg || `Failed to ${makeLocked ? "lock" : "unlock"} tier`,
        );

      setLockedTiers((prev) => {
        const next = [...prev];
        next[idx] = makeLocked;
        return next;
      });

      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message={
              makeLocked
                ? `Tier ${tierNumber} locked at ${tierBoundaries[idx]}`
                : `Tier ${tierNumber} unlocked — you can drag or type a new range`
            }
          />
        ),
        placement: "top",
        duration: 2500,
      });
    } catch (error) {
      const status = error?.response?.status;
      const action = makeLocked ? "lock" : "unlock";
      const msg =
        status === 404
          ? `${titleCase(action)} endpoint not found (404). Double-check the tier-config/:tierNumber/${action} route is deployed at this API base URL.`
          : error?.response?.data?.status_msg ||
            error?.message ||
            `Failed to ${action} tier`;
      toast.show({
        render: () => <Toast color={GlobalStyles.colors.red} message={msg} />,
        placement: "top",
        duration: 5000,
      });
    } finally {
      setLockingTierNumber(null);
    }
  };

  const handleLockTier = (tierNumber) =>
    persistTierLayoutAndToggle(tierNumber, true);

  const handleUnlockTier = (tierNumber) =>
    persistTierLayoutAndToggle(tierNumber, false);

  const saveConfiguration = async () => {
    try {
      setIsSaving(true);
      const numTiers = Math.max(2, Math.min(10, safeInt(numTiersStr, 3)));

      const configData = {
        user_type: "business",
        loyalty_type: "points",
        calculation_type: "fixed",
        points_per_dollar_points: safeNum(pointsValue, 2),
        points_per_dollar_dollars: safeNum(dollarsValue, 1),
        tiers_enabled: Boolean(haveTiers),
        number_of_tiers: haveTiers ? numTiers : 0,

        total_points_cap:
          totalPointsCap !== "" ? safeNum(totalPointsCap, null) : null,
        is_active: true,
      };

      const res = unwrap(
        await configurationApi.updateUserConfig(user.token, configData),
      );
      if (!res.status) throw new Error(res.status_msg || "Save failed");

      if (haveTiers) {
        const tiersPayload = boundariesToTiersPayload(
          tierBoundaries,
          numTiers,
          tierNames,
        );
        await configurationApi
          .saveTierConfig(user.token, tiersPayload)
          .catch((e) => {
            if (!is404(e))
              console.warn("saveTierConfig failed:", e?.message);
          });
      }

      configurationApi
        .updateCommonPoolSettings(user.token, joinCommonPool)
        .catch((e) => {
          if (!is404(e))
            console.warn("updateCommonPoolSettings failed:", e?.message);
        });

      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message={"Settings saved successfully!"}
          />
        ),
        placement: "top",
        duration: 3000,
      });

      setStep(0);
      setTimeout(() => loadConfiguration(), 600);
    } catch (error) {
      const msg =
        error?.response?.data?.status_msg ||
        error?.message ||
        "Failed to save settings.";
      toast.show({
        render: () => <Toast color={GlobalStyles.colors.red} message={msg} />,
        placement: "top",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Center flex={1} style={styles.pageBg}>
        <Spinner size="lg" color={GlobalStyles.colors.blue} />
        <Text mt={4}>Loading configuration...</Text>
      </Center>
    );
  }

  return (
    <Box flex={1} pb="48px">
      {step === 0 && (
        <LoyaltySettingsStep1
          locked={locked}
          joinCommonPool={joinCommonPool}
          setJoinCommonPool={setJoinCommonPool}
          haveTiers={haveTiers}
          setHaveTiers={setHaveTiers}
          pointsValue={pointsValue}
          setPointsValue={setPointsValue}
          dollarsValue={dollarsValue}
          setDollarsValue={setDollarsValue}
          totalPointsCap={totalPointsCap}
          setTotalPointsCap={setTotalPointsCap}
          onNext={() => setStep(1)}
          onSave={saveConfiguration}
          isSaving={isSaving}
        />
      )}

      {step === 1 && haveTiers && (
        <TiersSetupCard
          locked={locked}
          numTiersStr={numTiersStr}
          setNumTiersStr={setNumTiersStr}
          tierBoundaries={tierBoundaries}
          setTierBoundaries={setTierBoundaries}
          tierNames={tierNames}
          lockedTiers={lockedTiers}
          lockingTierNumber={lockingTierNumber}
          onLockTier={handleLockTier}
          onUnlockTier={handleUnlockTier}
          onNext={() => setStep(2)}
          onBack={() => setStep(0)}
        />
      )}

      {step === 2 && haveTiers && (
        <NameTiersCard
          locked={locked}
          numTiers={Math.max(2, Math.min(10, safeInt(numTiersStr, 3)))}
          tierNames={tierNames}
          setTierNames={setTierNames}
          onBack={() => setStep(1)}
          onSave={saveConfiguration}
          isSaving={isSaving}
        />
      )}
    </Box>
  );
};

const AnalyticsRow = ({ label, value }) => (
  <HStack
    alignItems="center"
    justifyContent="space-between"
    w="100%"
    flexWrap="wrap"
    space={2}
  >
    <Text
      numberOfLines={2}
      style={{
        fontFamily: "Roboto, Arial, sans-serif",
        fontWeight: "400",
        fontSize: 14,
        lineHeight: 20,
        letterSpacing: 0.1,
        color: GlobalStyles.colors.black,
        verticalAlign: "middle",
      }}
      flexShrink={1}
    >
      {label}
    </Text>
    <Box
      mt={{ base: 2, md: 0 }}
      w="100.3px"
      h="30px"
      borderWidth={1}
      borderColor={GlobalStyles.colors.black}
      borderRadius={5}
      backgroundColor={GlobalStyles.colors.white}
      alignItems="center"
      justifyContent="center"
      px={2}
    >
      <Text
        style={{
          fontFamily: "Roboto, Arial, sans-serif",
          fontWeight: "400",
          fontSize: 18,
          lineHeight: 20,
          letterSpacing: 0.1,
          color: GlobalStyles.colors.black,
          textAlign: "center",
        }}
      >
        {value}
      </Text>
    </Box>
  </HStack>
);

const LoyaltyAnalytics = ({ toast, user }) => {
  const [period, setPeriod] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({
    loyalty: 0,
    silver: 0,
    gold: 0,
    diamond: 0,
    purchaseByLoyal: 0,
    redeemsInPoints: 0,
    redeemsInDollars: 0,
    bonusGiven: 0,
    outsidePct: 0,
  });

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    if (!user?.token) return;
    try {
      setIsLoading(true);
      const res = unwrap(
        await dashboardApi.getUserDashboard(user.token, period),
      );
      if (res.status && res.data) {
        const d = res.data;
        setAnalyticsData({
          loyalty: safeInt(d.total_customers_on_loyalty, 0),
          silver: safeInt(d.total_customers_on_silver, 0),
          gold: safeInt(d.total_customers_on_gold, 0),
          diamond: safeInt(d.total_customers_on_diamond, 0),
          purchaseByLoyal: safeInt(
            d.total_purchase_by_loyal ??
              d.total_purchase_by_loyal_customers,
            0,
          ),
          redeemsInPoints: safeInt(
            d.total_redeems_in_points ??
              d.total_redeems_by_loyal_customers_in_points,
            0,
          ),
          redeemsInDollars: safeInt(
            d.total_redeems_in_dollars ??
              d.total_redeems_by_loyal_customers_in_dollars,
            0,
          ),
          bonusGiven: safeInt(d.bonus_points_given, 0),
          outsidePct: safeInt(d.percent_purchases_outside_loyalty, 0),
        });
      }
    } catch (error) {
      console.error("Error loading analytics:", error);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Failed to load analytics"}
          />
        ),
        placement: "top",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Center flex={1} style={styles.pageBg}>
      <Box w="100%" maxW="630px" alignSelf="center" pt="20px" px={4}>
        <HStack
          w="100%"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Text
            style={{
              fontFamily: "Roboto, Arial, sans-serif",
              fontWeight: "400",
              fontStyle: "normal",
              fontSize: 19,
              lineHeight: 20,
              letterSpacing: 0.1,
              color: GlobalStyles.colors.black,
              textAlign: "left",
              verticalAlign: "middle",
            }}
          >
            Dash-Board – Analytics
          </Text>
          <Select
            selectedValue={period}
            minWidth="120"
            accessibilityLabel="Select Period"
            placeholder="Select Period"
            _selectedItem={{
              bg: GlobalStyles.colors.blue,
              endIcon: <CheckIcon size="5" />,
            }}
            mt={1}
            onValueChange={(itemValue) => setPeriod(itemValue)}
          >
            <Select.Item label="All Time" value="all" />
            <Select.Item label="This Week" value="week" />
            <Select.Item label="This Month" value="month" />
            <Select.Item label="This Year" value="year" />
          </Select>
        </HStack>
        <Box
          w="100%"
          h="52px"
          bg="#D9D9D9"
          borderWidth={0.2}
          borderColor={GlobalStyles.colors.black}
          justifyContent="center"
        >
          <Text
            style={{
              fontFamily: "Roboto, Arial, sans-serif",
              fontWeight: "400",
              fontStyle: "normal",
              fontSize: 18,
              lineHeight: 20,
              letterSpacing: 0.1,
              color: GlobalStyles.colors.black,
              textAlign: "center",
              verticalAlign: "middle",
            }}
          >
            Select Period
          </Text>
        </Box>
        {isLoading ? (
          <Center
            w="100%"
            minH="396px"
            bg="#D9D9D940"
            borderWidth={0.2}
            borderColor={GlobalStyles.colors.black}
          >
            <Spinner size="lg" color={GlobalStyles.colors.blue} />
          </Center>
        ) : (
          <Box
            w="100%"
            minH="396px"
            bg="#D9D9D940"
            borderWidth={0.2}
            borderColor={GlobalStyles.colors.black}
            overflow="hidden"
          >
            <VStack
              w="100%"
              px="25px"
              pt="22px"
              pb="22px"
              space={0}
              style={{ rowGap: 9 }}
            >
              <AnalyticsRow label="Total Customers On Loyalty" value={analyticsData.loyalty} />
              <AnalyticsRow label="Total Customers On Silver" value={analyticsData.silver} />
              <AnalyticsRow label="Total Customers On Gold" value={analyticsData.gold} />
              <AnalyticsRow label="Total Customers On Diamond" value={analyticsData.diamond} />
              <AnalyticsRow label="Total Purchase By Loyal Customers" value={analyticsData.purchaseByLoyal} />
              <AnalyticsRow label="Total Redeems By Loyal Customers in points" value={analyticsData.redeemsInPoints} />
              <AnalyticsRow label="Total Redeems By Loyal Customers in dollars" value={analyticsData.redeemsInDollars} />
              <AnalyticsRow label="Bonus Points Given" value={analyticsData.bonusGiven} />
              <AnalyticsRow label="% Purchases outside loyalty" value={analyticsData.outsidePct} />
            </VStack>
          </Box>
        )}
      </Box>
    </Center>
  );
};

const extractAvailablePointsFromSummary = (resData) => {
  const d = resData || {};
  const candidates = [
    d.points_available,
    d.pointsAvailable,
    d.available_points,
    d.availablePoints,
    d.total_points,
    d.totalPoints,
    d.points,
    d?.summary?.points_available,
    d?.data?.points_available,
  ];
  const n = candidates
    .map((x) => safeNum(x, NaN))
    .find((x) => Number.isFinite(x));
  return Number.isFinite(n) ? n : 0;
};

const computeIssuedPoints = ({
  cashAmount,
  pointsPerDollarPoints,
  pointsPerDollarDollars,
}) => {
  const dollars = safeNum(cashAmount, NaN);
  const pts = safeNum(pointsPerDollarPoints, NaN);
  const dlrs = safeNum(pointsPerDollarDollars, NaN);
  if (
    !Number.isFinite(dollars) || dollars <= 0 ||
    !Number.isFinite(pts) || pts <= 0 ||
    !Number.isFinite(dlrs) || dlrs <= 0
  )
    return 0;
  return Math.round((dollars / dlrs) * pts * 100) / 100;
};

const RedeemPoints = ({ toast, user }) => {
  const [amount, setAmount] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [currentPurchase, setCurrentPurchase] = useState("");
  const [oldPts, setOldPts] = useState("0.00");
  const [newPts, setNewPts] = useState("0.00");
  const [isProcessing, setIsProcessing] = useState(false);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    if (!user?.token) return;
    configurationApi
      .getUserConfig(user.token)
      .then((raw) => {
        const r = unwrap(raw);
        if (r.status && r.data) setCfg(r.data);
      })
      .catch(() => {});
  }, [user?.token]);

  useEffect(() => {
    if (!user?.token) return;
    const id = safeInt(String(customerId || "").trim(), NaN);
    if (!Number.isFinite(id) || id < 1) return;
    const t = setTimeout(async () => {
      try {
        const res = unwrap(
          await pointsApi.getUserPointsSummary(user.token, id),
        );
        if (res.status && res.data) {
          const avail = extractAvailablePointsFromSummary(res.data);
          setOldPts(formatPointsLike(avail));
          setNewPts(formatPointsLike(avail));
        }
      } catch {}
    }, 350);
    return () => clearTimeout(t);
  }, [customerId, user?.token]);

  useEffect(() => {
    if (isProcessing) return;
    const id = safeInt(String(customerId || "").trim(), NaN);
    if (!Number.isFinite(id) || id < 1) return;
    const oldN = safeNum(oldPts, NaN);
    if (!Number.isFinite(oldN)) return;
    const pts = safeNum(String(amount || "").replace("$", "").trim(), NaN);
    if (Number.isFinite(pts) && pts > 0) {
      setNewPts(formatPointsLike(Math.max(0, oldN - pts)));
    } else {
      setNewPts(formatPointsLike(oldN));
    }
  }, [amount, customerId, oldPts, isProcessing]);

  const currentDiscountDisplay = useMemo(() => {
    const pts = safeNum(String(amount || "").replace("$", "").trim(), NaN);
    const ppp = safeNum(cfg?.points_per_dollar_points, NaN);
    const ppd = safeNum(cfg?.points_per_dollar_dollars, NaN);
    if (
      !Number.isFinite(pts) || pts <= 0 ||
      !Number.isFinite(ppp) || ppp <= 0 ||
      !Number.isFinite(ppd) || ppd <= 0
    ) {
      return "$0.00";
    }
    const dollars = (pts / ppp) * ppd;
    return `$${dollars.toFixed(2)}`;
  }, [amount, cfg]);

  const handleRedeemPoints = async () => {
    const pts = safeNum(String(amount || "").replace("$", "").trim(), NaN);
    const customerUserId = safeInt(String(customerId || "").trim(), NaN);

    if (!Number.isFinite(customerUserId) || customerUserId < 1) {
      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={"Please enter a valid Customer ID"} />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    if (!Number.isFinite(pts) || pts <= 0) {
      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={"Please enter a valid points amount to redeem"} />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }

    try {
      setIsProcessing(true);

      try {
        const oldRes = unwrap(
          await pointsApi.getUserPointsSummary(user.token, customerUserId),
        );
        if (oldRes.status && oldRes.data)
          setOldPts(formatPointsLike(extractAvailablePointsFromSummary(oldRes.data)));
      } catch {}
      const res = unwrap(
        await pointsApi.redeem(user.token, pts, null),
      );

      if (res.status) {
        const updatedAvail = safeNum(
          res.data?.loyalty_points?.points_available,
          NaN,
        );
        if (Number.isFinite(updatedAvail)) {
          setNewPts(formatPointsLike(updatedAvail));
        } else {
          try {
            const newRes = unwrap(
              await pointsApi.getUserPointsSummary(user.token, customerUserId),
            );
            if (newRes.status && newRes.data)
              setNewPts(formatPointsLike(extractAvailablePointsFromSummary(newRes.data)));
          } catch {}
        }

        const redeemedAmt = safeNum(res.data?.points_redeemed, pts);
        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.green}
              message={`Points redeemed successfully! ${formatPointsLike(redeemedAmt)} points.`}
            />
          ),
          placement: "top",
          duration: 3000,
        });

        setAmount("");
        setCustomerId("");
        setCurrentPurchase("");
      } else {
        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.red}
              message={res.status_msg || "Failed to redeem points"}
            />
          ),
          placement: "top",
          duration: 4000,
        });
      }
    } catch (error) {
      const apiMsg =
        error?.response?.data?.status_msg || "Failed to redeem points";
      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={apiMsg} />
        ),
        placement: "top",
        duration: 5000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Center flex={1} style={styles.pageBg}>
      <Box w="100%" maxW="1200px" mx="auto" px={4}>
        <Box alignSelf="center" mb={4}>
          <Text
            style={{
              fontFamily: "Roboto, Arial, sans-serif",
              fontWeight: "400",
              fontSize: 18,
              lineHeight: 20,
              letterSpacing: 0.1,
              color: GlobalStyles.colors.black,
            }}
          >
            Scan QR code from any QR mobile app
          </Text>
        </Box>
        <Box
          width="100%"
          maxW={509}
          alignSelf="center"
          bg={GlobalStyles.colors.white}
          borderRadius="12px"
          shadow={2}
          pb={6}
        >
          <VStack
            w="100%"
            alignItems="center"
            justifyContent="flex-start"
            px="28px"
          >
            <Box h="22px" />

            <VStack w="100%" space={3}>
              {[
                {
                  label: "Customer ID:",
                  value: customerId,
                  setter: setCustomerId,
                  readOnly: false,
                  keyboard: "numeric",
                },
                {
                  label: "Current Purchase:",
                  value: currentPurchase,
                  setter: setCurrentPurchase,
                  readOnly: false,
                  keyboard: "numeric",
                },
                {
                  label: "Enter Points to Redeem:",
                  value: amount,
                  setter: setAmount,
                  readOnly: false,
                  keyboard: "numeric",
                },
                {
                  label: "Old Points before redeem:",
                  value: oldPts,
                  setter: null,
                  readOnly: true,
                  keyboard: "numeric",
                },
                {
                  label: "New Points after redeem:",
                  value: newPts,
                  setter: null,
                  readOnly: true,
                  keyboard: "numeric",
                },
              ].map(({ label, value, setter, readOnly, keyboard }) => (
                <HStack
                  key={label}
                  w="100%"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Text
                    style={{
                      fontFamily: "Roboto, Arial, sans-serif",
                      fontWeight: "400",
                      fontSize: 14,
                      lineHeight: 18,
                      color: GlobalStyles.colors.black,
                    }}
                  >
                    {label}
                  </Text>
                  <Box
                    w="200px"
                    h="32px"
                    borderWidth={1}
                    borderColor="#CCCCCC"
                    bg={GlobalStyles.colors.white}
                    justifyContent="center"
                    px={2}
                  >
                    <Input
                      value={value}
                      onChangeText={readOnly ? undefined : setter}
                      isReadOnly={readOnly}
                      variant="unstyled"
                      fontSize={14}
                      color={GlobalStyles.colors.black}
                      keyboardType={keyboard}
                      style={{ fontFamily: "Roboto, Arial, sans-serif" }}
                    />
                  </Box>
                </HStack>
              ))}

              <HStack
                w="100%"
                alignItems="center"
                justifyContent="space-between"
                mt={1}
              >
                <Text
                  style={{
                    fontFamily: "Roboto, Arial, sans-serif",
                    fontWeight: "600",
                    fontSize: 14,
                    lineHeight: 18,
                    color: GlobalStyles.colors.black,
                  }}
                >
                  Current Discount:
                </Text>
                <Box
                  w="200px"
                  h="32px"
                  borderWidth={1}
                  borderColor={GlobalStyles.colors.blue}
                  bg={GlobalStyles.colors.white}
                  justifyContent="center"
                  px={2}
                >
                  <Text
                    style={{
                      fontFamily: "Roboto, Arial, sans-serif",
                      fontWeight: "bold",
                      fontSize: 14,
                      color: GlobalStyles.colors.blue,
                    }}
                  >
                    {currentDiscountDisplay}
                  </Text>
                </Box>
              </HStack>
            </VStack>

            <HStack mt={5} space={3} justifyContent="center">
              <Button
                bg={GlobalStyles.colors.blue}
                onPress={handleRedeemPoints}
                isLoading={isProcessing}
                isLoadingText="Processing..."
                _text={{
                  color: GlobalStyles.colors.white,
                  fontWeight: "bold",
                  fontSize: 14,
                  fontFamily: "Roboto, Arial, sans-serif",
                }}
                borderRadius={6}
                px={4}
                h={10}
              >
                Redeem Points
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Box>
    </Center>
  );
};

const LoyaltyManager = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState("settings");
  const toast = useToast();
  const user = useSelector((state) => state.user.user);
  const [settingsBtnTextColor, setSettingsBtnTextColor] = useState(
    GlobalStyles.colors.mustard,
  );
  const [analyticsBtnTextColor, setAnalyticsBtnTextColor] = useState(
    GlobalStyles.colors.blue,
  );
  const [redeemBtnTextColor, setRedeemBtnTextColor] = useState(
    GlobalStyles.colors.blue,
  );
  function changeTabColor(tabType) {
    setSettingsBtnTextColor(
      tabType === "settings"
        ? GlobalStyles.colors.mustard
        : GlobalStyles.colors.blue,
    );
    setAnalyticsBtnTextColor(
      tabType === "analytics"
        ? GlobalStyles.colors.mustard
        : GlobalStyles.colors.blue,
    );
    setRedeemBtnTextColor(
      tabType === "redeem"
        ? GlobalStyles.colors.mustard
        : GlobalStyles.colors.blue,
    );
  }

  useEffect(() => {
    changeTabColor("settings");
    if (user?.token)
      axios.defaults.headers.common.Authorization = `Bearer ${user.token}`;
  }, [user]);

  return (
    <Box
      w="100%"
      bg={GlobalStyles.colors.lightGrey}
      minH={Platform.OS === "web" ? "100vh" : "100%"}
    >
      <Center>
        <Box w={["92%", 600, 860]} maxW="960px">
          {onBack && (
            <HStack w="100%" mb={3} px={4} mt={4}>
              <Pressable onPress={onBack}>
                <HStack alignItems="center" space={2}>
                  <Text
                    color={GlobalStyles.colors.blue}
                    fontWeight="500"
                    fontSize={14}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    ← Back to Services
                  </Text>
                </HStack>
              </Pressable>
            </HStack>
          )}
          <HStack
            space={2}
            mb={6}
            justifyContent="center"
            flexWrap="wrap"
          >
            <Button
              size="sm"
              mx={1}
              my={1}
              variant="solid"
              px={4}
              bg={GlobalStyles.colors.white}
              borderColor={settingsBtnTextColor}
              borderWidth={2}
              borderRadius={40}
              _hover={{ bg: GlobalStyles.colors.grey }}
              onPress={() => {
                setActiveTab("settings");
                changeTabColor("settings");
              }}
            >
              <Text
                fontWeight="600"
                fontFamily={GlobalStyles.text.fontFamily}
                color={settingsBtnTextColor}
              >
                Settings
              </Text>
            </Button>
            <Button
              size="sm"
              mx={1}
              my={1}
              variant="solid"
              px={4}
              bg={GlobalStyles.colors.white}
              borderColor={analyticsBtnTextColor}
              borderWidth={2}
              borderRadius={40}
              _hover={{ bg: GlobalStyles.colors.grey }}
              onPress={() => {
                setActiveTab("analytics");
                changeTabColor("analytics");
              }}
            >
              <Text
                fontWeight="600"
                fontFamily={GlobalStyles.text.fontFamily}
                color={analyticsBtnTextColor}
              >
                Analytics
              </Text>
            </Button>
            <Button
              size="sm"
              mx={1}
              my={1}
              variant="solid"
              px={4}
              bg={GlobalStyles.colors.white}
              borderColor={redeemBtnTextColor}
              borderWidth={2}
              borderRadius={40}
              _hover={{ bg: GlobalStyles.colors.grey }}
              onPress={() => {
                setActiveTab("redeem");
                changeTabColor("redeem");
              }}
            >
              <Text
                fontWeight="600"
                fontFamily={GlobalStyles.text.fontFamily}
                color={redeemBtnTextColor}
              >
                Redeem Points
              </Text>
            </Button>
          </HStack>
        </Box>
      </Center>
      <Box pb="48px">
        {activeTab === "settings" && (
          <LoyaltySettings toast={toast} user={user} />
        )}
        {activeTab === "analytics" && (
          <LoyaltyAnalytics toast={toast} user={user} />
        )}
        {activeTab === "redeem" && (
          <RedeemPoints toast={toast} user={user} />
        )}
      </Box>
    </Box>
  );
};

export default LoyaltyManager;